import { ARTIFACTS_PATH, type CreativeLearningResource } from '@studio/shared';
import { useState, type ReactNode } from 'react';
import { assetTypeLabel, outcomeLabel, targetLabel } from '../../app/history.js';
import { api } from '../../shell/api.js';
import { Link } from '../../shell/router.js';

/**
 * R148. What a Creative Learning keeps, on the Learning itself.
 *
 * Everything below has been on the resource since task 10 and the interface rendered the
 * App's name and nothing else - so an observation the operator was asked to trust arrived
 * with no way to see what it was grounded in. The grounding is: which App, Target and
 * Asset Type it came out of, the Hypothesis that was being tested, the two creatives that
 * were compared, how it went, and the way back to the evidence.
 *
 * There are no stars and no confidence percentage of the Studio's own making, and no
 * provider, prompt, seed or model appears anywhere on this surface.
 */

/**
 * The comparison, both sides, at a size that lets them be compared.
 *
 * A blind acceptance never once saw this pair: one side was a blank frame every time,
 * because a screenshot set has no single Artifact to freeze. Both sides are lists now,
 * and both are drawn at a fixed thumbnail height - the same walk measured the Control on
 * this page at 1174x2087 CSS pixels and the page at 8457, which is a record that fits on
 * a screen and a half rendered as a poster.
 */
function ComparisonSide({ label, artifactIds }: { label: string; artifactIds: readonly string[] }): ReactNode {
  return (
    <div className="comparison-side">
      <p className="eyebrow">{artifactIds.length > 1 ? `${label} · ${artifactIds.length}` : label}</p>
      {artifactIds.length === 0 ? (
        <p className="note">Не зафиксировано</p>
      ) : (
        <ul className="thumb-strip" aria-label={`Креатив ${label}`}>
          {artifactIds.map((artifactId, index) => (
            <li key={artifactId}>
              <img
                className="thumb"
                src={`${ARTIFACTS_PATH}/${artifactId}`}
                alt={artifactIds.length === 1 ? `Креатив ${label}` : `Креатив ${label} ${index + 1}`}
                loading="lazy"
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** The grounding, compact. One component, mounted on the card and on the shelf. */
export function LearningGrounding({ learning }: { learning: CreativeLearningResource }): ReactNode {
  /**
   * Every number, not only the one key the extractor happens to know by that name.
   *
   * `Add a number` used to write a fact nothing on any surface read back, so a figure
   * typed in by hand vanished for everyone looking at it. A fact with a value is a fact
   * the operator either kept or wrote, and it is shown.
   */
  const numbers = learning.facts.filter((fact) => fact.value.trim() !== '');
  return (
    <div className="learning-grounding">
      <p className="note">
        {`${learning.appDisplayName} · ${assetTypeLabel(learning.assetType)} · ${targetLabel(learning.target)} · ${learning.resultDate.slice(0, 10)}`}
      </p>
      {learning.hypothesis === null ? null : <p className="note">{`Hypothesis: ${learning.hypothesis}`}</p>}
      <p className="note">
        {[
          outcomeLabel(learning.semantics),
          ...numbers.map((fact) => `${fact.label} ${fact.value}${fact.unit ?? ''}`),
        ].join(' · ')}
      </p>

      {learning.controlArtifactIds.length === 0 && learning.candidateArtifactIds.length === 0 ? null : (
        <div className="learning-comparison">
          <ComparisonSide label="Control" artifactIds={learning.controlArtifactIds} />
          <ComparisonSide label="Candidate" artifactIds={learning.candidateArtifactIds} />
        </div>
      )}

      {learning.experimentId === null ? (
        <p className="note">Experiment за этим уже нет. Наблюдение остаётся как было.</p>
      ) : (
        <Link to={`/experiments/${learning.experimentId}/learn`} className="secondary-link">
          {learning.evidenceCount === 0
            ? 'Открыть Experiment, из которого это'
            : `Открыть evidence · приложено ${learning.evidenceCount}`}
        </Link>
      )}
    </div>
  );
}

export function LearningCard({
  learning,
  onChanged,
}: {
  learning: CreativeLearningResource;
  onChanged: () => void;
}): ReactNode {
  const [wording, setWording] = useState(learning.statement);
  const [busy, setBusy] = useState(false);

  const save = async (): Promise<void> => {
    setBusy(true);
    try {
      await api.updateLearning(learning.id, { statement: wording });
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  const setState = async (state: 'active' | 'retired'): Promise<void> => {
    setBusy(true);
    try {
      await api.updateLearning(learning.id, { state });
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="learning-card" aria-label="Creative Learning">
      <p className="eyebrow">
        {learning.state === 'retired' ? 'Creative Learning · снято' : 'Creative Learning'}
        {learning.mixedSignal ? ' · Смешанный сигнал' : ''}
      </p>
      {learning.mixedSignal ? (
        <p className="note">
          Другой Experiment по этому App, Target и Asset Type нашёл обратное. Оба сохранены как были.
        </p>
      ) : null}

      <label htmlFor={`learning-${learning.id}`}>Формулировка</label>
      <textarea
        id={`learning-${learning.id}`}
        rows={3}
        value={wording}
        onChange={(event) => setWording(event.target.value)}
      />
      <p className="note">{`Изначально: ${learning.originalStatement}`}</p>

      <LearningGrounding learning={learning} />

      <ul className="provenance" aria-label="Evidence за этим Creative Learning">
        {learning.provenance.map((fact) => (
          <li key={fact.code}>{fact.statement}</li>
        ))}
      </ul>

      <div className="learning-actions">
        <button type="button" className="ghost" disabled={busy || wording === learning.statement} onClick={() => void save()}>
          Сохранить формулировку
        </button>
        {learning.state === 'active' ? (
          <button type="button" className="ghost" disabled={busy} onClick={() => void setState('retired')}>
            Снять это Creative Learning
          </button>
        ) : (
          <button type="button" className="ghost" disabled={busy} onClick={() => void setState('active')}>
            Восстановить это Creative Learning
          </button>
        )}
      </div>
    </section>
  );
}
